import { useTranslation } from 'next-i18next';
import * as React from 'react';
import { ReactElement } from 'react';
import Tooltip from 'react-tooltip-lite';

import { useGetAttendanceSummary } from '@/modules/workforce/workersTimesheet/apiHooks';
import WidgetItem from '@/modules/workforce/workersTimesheet/components/AttendedWidgets/WidgetItem';
import { IWorkersTimesheet } from '@/modules/workforce/workersTimesheet/types';
import { Typography } from '@/shared/components/atoms';
import { useStore } from '@/shared/hooks/context/createGenericContext';

import ExleminationMark from '~/images/shared/ExleminationMark.svg';
import Eye from '~/images/shared/Eye.svg';
import Fraud from '~/images/shared/fraud.svg';
import Clock from '~/images/shared/new-clock.svg';
import Workers from '~/images/shared/new-group-person.svg';
import Crew from '~/images/shared/new-single-person.svg';
import Warning from '~/images/shared/warning-2.svg';

interface IAttendedWidgetsProps {
  date: string;
}
export default function AttendedWidgets({
  date,
}: IAttendedWidgetsProps): ReactElement {
  // TODO: cache data
  const [selectedAsset] = useStore((store) => store?.asset);
  const { t } = useTranslation('common');
  const { data, isLoading } = useGetAttendanceSummary({
    entityId: selectedAsset?.id,
    date,
  });

  return isLoading ? (
    <></>
  ) : (
    <section className='mt-32  grid grid-cols-1 gap-16 tablet:grid-cols-3 desktop:grid-cols-5'>
      <WidgetItem
        data={{
          title: t('crew-attended'),
          value: data?.crewsAttended,
          icon: Crew,
        }}
      />
      <WidgetItem
        data={{
          title: t('workers-attended'),
          value: data?.workersAttended,
          icon: Workers,
          ValueTooltip:
            Object.keys(data?.workersAttendedBreakdown || {}).length > 0 ? (
              <WrokersBreakdownTooltip
                workersAttendedBreakdown={data?.workersAttendedBreakdown}
              />
            ) : undefined,
        }}
      />
      <WidgetItem
        valueColor='text-tertiary-default'
        data={{
          title: t('suspicious'),
          value: data?.suspicious,
          icon: Warning,
          TitleTooltip: <SuspiciousTooltip />,
        }}
      />
      <WidgetItem
        valueColor='text-error-400'
        data={{
          title: t('fraud'),
          value: data?.fraud,
          icon: Fraud,
        }}
      />
      <WidgetItem
        data={{
          title: t('total-hours'),
          value: data?.totalHours,
          icon: Clock,
        }}
      />
    </section>
  );
}
export const SuspiciousTooltip = () => {
  const { t } = useTranslation('common');
  return (
    <Tooltip
      content={
        <Typography
          variant='body2'
          className='w-230 rounded-lg bg-background-200 px-16 py-4 text-center'
        >
          {t('suspicious-description')}
        </Typography>
      }
      direction='down'
      arrow={false}
      tipContentHover
    >
      <span className='text-16 text-background-400'>
        <ExleminationMark />
      </span>
    </Tooltip>
  );
};

interface IWorkersBreakdownProps {
  workersAttendedBreakdown?: IWorkersTimesheet.IWorkersAttendedBreakdown;
}
export const WrokersBreakdownTooltip = ({
  workersAttendedBreakdown,
}: IWorkersBreakdownProps) => {
  const { t } = useTranslation('common');
  return (
    <Tooltip
      content={
        <div className='relative max-h-[70vh] w-260 overflow-hidden overflow-y-auto rounded-xl bg-white font-Cairo shadow-200'>
          <div className='sticky top-0 left-0 flex items-center justify-between bg-white px-24  py-16 capitalize text-surface-10'>
            <Typography variant='caption' weight='medium'>
              {t('crew')}
            </Typography>
            <Typography variant='caption' weight='medium'>
              {t('workers')}
            </Typography>
          </div>
          <div className='flex flex-col'>
            {Object.entries(workersAttendedBreakdown || {}).map(
              ([key, value], index) => (
                <div
                  key={index}
                  className='flex items-center  justify-between px-24  py-16 capitalize even:bg-neutralVariant-99 '
                >
                  <Typography variant='caption' weight='semiBold'>
                    {key}
                  </Typography>
                  <Typography variant='caption'>{value}</Typography>
                </div>
              )
            )}
          </div>
        </div>
      }
      direction='down'
      arrow={false}
      tipContentHover
    >
      <span className='text-28 text-background-400'>
        <Eye />
      </span>
    </Tooltip>
  );
};
